import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { getPermissions } from '../../core/store/account/selectors';
import { AppState } from '../../core/store/store.module';

/**
 * Normalizza il valore dell'input in un array di permessi richiesti.
 */
function toRequired(value: string | string[]): string[] {
  return Array.isArray(value) ? value : (value ? [value] : []);
}

/**
 * Vero se `permissions` contiene almeno uno dei permessi `required` (OR).
 * Con `required` vuoto ritorna sempre true.
 */
function hasAny(required: string[], permissions: string[]): boolean {
  if (!required.length) {
    return true;
  }
  return required.some(permission => permissions.indexOf(permission) !== -1);
}

/**
 * Mostra l'elemento se l'utente possiede almeno uno dei permessi indicati (OR).
 *
 * Uso:
 *   <div *ngCan="'clienti'">...</div>
 *   <div *ngCan="['clienti', 'preventivi.prodotti-aggiuntivi']">...</div>
 *
 * NOTA: direttiva volutamente SELF-CONTAINED (niente classe base astratta
 * condivisa). L'ereditarietà da una base con DI/lifecycle non è affidabile in
 * build AOT (Angular 8): funziona in JIT/`ng serve` ma i permessi non venivano
 * cablati dopo la build. Tenere DI, lifecycle e logica nella classe concreta.
 */
@Directive({
  selector: '[ngCan]',
})
export class NgCanDirective implements OnInit, OnDestroy {

  private required: string[] = [];
  private permissions: string[] = [];
  private hasView = false;
  private subscription: Subscription;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private store: Store<AppState>,
  ) {}

  @Input()
  set ngCan(value: string | string[]) {
    this.required = toRequired(value);
    this.updateView();
  }

  ngOnInit() {
    this.subscription = this.store.select(getPermissions).subscribe((permissions: string[]) => {
      this.permissions = permissions || [];
      this.updateView();
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private updateView() {
    const show = hasAny(this.required, this.permissions);
    if (show && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!show && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}

/**
 * Mostra l'elemento se l'utente NON possiede nessuno dei permessi indicati.
 *
 * Uso:
 *   <div *ngCanNot="'gestione'">...</div>
 */
@Directive({
  selector: '[ngCanNot]',
})
export class NgCanNotDirective implements OnInit, OnDestroy {

  private required: string[] = [];
  private permissions: string[] = [];
  private hasView = false;
  private subscription: Subscription;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private store: Store<AppState>,
  ) {}

  @Input()
  set ngCanNot(value: string | string[]) {
    this.required = toRequired(value);
    this.updateView();
  }

  ngOnInit() {
    this.subscription = this.store.select(getPermissions).subscribe((permissions: string[]) => {
      this.permissions = permissions || [];
      this.updateView();
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private updateView() {
    const show = !hasAny(this.required, this.permissions);
    if (show && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!show && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}
